import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MdAdd,
  MdDeleteForever,
  MdEdit,
  MdExpandMore,
  MdFolder,
  MdClose,
  MdHelp,
  MdQuestionAnswer,
  MdSearch,
} from 'react-icons/md';
import { postData, getData, deleteData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const PAGE_SIZE = 10;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

const EMPTY_TOPIC = { name: '', position: 0, isActive: true };
const EMPTY_FAQ = { topic: '', question: '', answer: '', position: 0, isActive: true };

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(255, 209, 2)' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(245, 216, 87, 1)' },
};
const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px' });

// Coloured title bar shared by the two add / edit dialogs.
const DialogHeader = ({ icon: Icon, accent, title, subtitle, onClose }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: `linear-gradient(135deg, ${accent}1f, ${accent}08)`, borderBottom: `3px solid ${accent}` }}>
    <Box sx={{ background: accent, color: '#fff', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{subtitle}</Typography>
    </Box>
    <IconButton onClick={onClose} aria-label="close" size="small" sx={{ color: '#64748b' }}><MdClose size={20} /></IconButton>
  </Box>
);

const StatusPill = ({ active }) => (
  <Chip size="small" label={active ? 'Active' : 'Hidden'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11.5, background: active ? '#dcfce7' : '#f1f5f9', color: active ? '#166534' : '#475569' }} />
);

function FAQs() {
  const [topics, setTopics] = useState([]);
  const [stats, setStats] = useState({ topics: 0, faqs: 0, activeFaqs: 0 });
  const [topicMeta, setTopicMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedTopic, setSelectedTopic] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [faqMeta, setFaqMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState({ topics: true, faqs: false });
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState('');

  // dialogs
  const [topicDialog, setTopicDialog] = useState({ open: false, editingId: '', data: EMPTY_TOPIC, errors: {} });
  const [faqDialog, setFaqDialog] = useState({ open: false, editingId: '', data: EMPTY_FAQ, errors: {} });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind: 'topic' | 'faq', item }
  const [deleting, setDeleting] = useState(false);

  const selected = topics.find((t) => t.id === selectedTopic);

  // ---------- data ----------
  const fetchTopics = useCallback(async (page) => {
    setLoading((p) => ({ ...p, topics: true }));
    try {
      const res = await getData('faqtopic/list', { page, limit: PAGE_SIZE }, authToken(), false);
      const list = Array.isArray(res?.topics)
        ? res.topics.map((t) => ({ id: t._id, name: t.name, isActive: t.isActive, position: t.position || 0, faqCount: t.faqCount || 0, activeFaqCount: t.activeFaqCount || 0 }))
        : [];
      setTopics(list);
      if (res?.stats) setStats(res.stats);
      const m = res?.meta || {};
      setTopicMeta({ page: m.page || 1, totalPages: m.totalPages || 1, total: m.total || list.length });
      setSelectedTopic((cur) => (cur && list.some((t) => t.id === cur) ? cur : list[0]?.id || ''));
      setError('');
    } catch (err) {
      setTopics([]);
      setError('Could not load topics. Please try again.');
    } finally {
      setLoading((p) => ({ ...p, topics: false }));
    }
  }, []);

  const fetchFaqs = useCallback(async (topicId, page) => {
    if (!topicId) { setFaqs([]); return; }
    setLoading((p) => ({ ...p, faqs: true }));
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await getData(`faq/list/${topicId}`, params, authToken(), false);
      const list = Array.isArray(res?.faqs)
        ? res.faqs.map((f) => ({ id: f._id, topic: f.topic, question: f.question, answer: f.answer, isActive: f.isActive, position: f.position || 0 }))
        : [];
      setFaqs(list);
      const m = res?.meta || {};
      setFaqMeta({ page: m.page || 1, totalPages: m.totalPages || 1, total: m.total || 0 });
    } catch (err) {
      setFaqs([]);
      setError('Could not load FAQs. Please try again.');
    } finally {
      setLoading((p) => ({ ...p, faqs: false }));
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchTopics(topicMeta.page); }, [topicMeta.page, fetchTopics]);
  useEffect(() => { fetchFaqs(selectedTopic, faqMeta.page); }, [selectedTopic, faqMeta.page, fetchFaqs]);

  // wait for the user to stop typing before searching
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setFaqMeta((p) => ({ ...p, page: 1 })); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const pickTopic = (id) => {
    setSelectedTopic(id);
    setFaqMeta((p) => ({ ...p, page: 1 }));
    setExpanded('');
  };
  const refreshAll = async () => { await Promise.all([fetchTopics(topicMeta.page), fetchFaqs(selectedTopic, faqMeta.page)]); };

  // ---------- topic actions ----------
  const openTopic = (topic) => setTopicDialog({
    open: true,
    editingId: topic?.id || '',
    data: topic ? { name: topic.name, position: topic.position, isActive: topic.isActive !== false } : EMPTY_TOPIC,
    errors: {},
  });
  const setTopicField = (patch) => setTopicDialog((d) => ({ ...d, data: { ...d.data, ...patch }, errors: {} }));

  const saveTopic = async (e) => {
    e.preventDefault();
    const { data, editingId } = topicDialog;
    if (!data.name.trim()) return setTopicDialog((d) => ({ ...d, errors: { name: 'Topic name is required' } }));
    setSaving(true);
    try {
      const payload = { name: data.name.trim(), position: Number(data.position) || 0, isActive: data.isActive };
      if (editingId) await postData(`faqtopic/update/${editingId}`, payload, authToken());
      else await postData('faqtopic/add', payload, authToken());
      setTopicDialog((d) => ({ ...d, open: false }));
      await fetchTopics(editingId ? topicMeta.page : 1);
      if (!editingId) setTopicMeta((p) => ({ ...p, page: 1 }));
    } catch (err) {
      setError('Could not save the topic. The name may already exist.');
    } finally {
      setSaving(false);
    }
  };

  const toggleTopic = async (topic) => {
    try {
      await postData(`faqtopic/update/${topic.id}`, { isActive: !topic.isActive }, authToken(), false);
      setTopics((list) => list.map((t) => (t.id === topic.id ? { ...t, isActive: !t.isActive } : t)));
    } catch (err) {
      setError('Could not change the topic status.');
    }
  };

  // ---------- FAQ actions ----------
  const openFaq = (faq) => setFaqDialog({
    open: true,
    editingId: faq?.id || '',
    data: faq
      ? { topic: faq.topic, question: faq.question, answer: faq.answer, position: faq.position, isActive: faq.isActive !== false }
      : { ...EMPTY_FAQ, topic: selectedTopic },
    errors: {},
  });
  const setFaqField = (patch) => setFaqDialog((d) => ({ ...d, data: { ...d.data, ...patch }, errors: {} }));

  const saveFaq = async (e) => {
    e.preventDefault();
    const { data, editingId } = faqDialog;
    const errors = {};
    if (!data.topic) errors.topic = 'Choose a topic';
    if (!data.question.trim()) errors.question = 'Question is required';
    if (!data.answer.trim()) errors.answer = 'Answer is required';
    if (Object.keys(errors).length) return setFaqDialog((d) => ({ ...d, errors }));
    setSaving(true);
    try {
      const payload = { topic: data.topic, question: data.question.trim(), answer: data.answer.trim(), position: Number(data.position) || 0, isActive: data.isActive };
      if (editingId) await postData(`faq/update/${editingId}`, payload, authToken());
      else await postData('faq/add', payload, authToken());
      setFaqDialog((d) => ({ ...d, open: false }));
      if (data.topic !== selectedTopic) setSelectedTopic(data.topic);
      await refreshAll();
    } catch (err) {
      setError('Could not save the FAQ. A question with the same text may already exist.');
    } finally {
      setSaving(false);
    }
  };

  const toggleFaq = async (faq) => {
    try {
      await postData(`faq/update/${faq.id}`, { isActive: !faq.isActive }, authToken(), false);
      setFaqs((list) => list.map((f) => (f.id === faq.id ? { ...f, isActive: !f.isActive } : f)));
      fetchTopics(topicMeta.page);
    } catch (err) {
      setError('Could not change the FAQ status.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { kind, item } = deleteTarget;
      await deleteData(`${kind === 'topic' ? 'faqtopic' : 'faq'}/delete/${item.id}`, authToken());
      setDeleteTarget(null);
      if (kind === 'topic') {
        if (topics.length === 1 && topicMeta.page > 1) setTopicMeta((p) => ({ ...p, page: p.page - 1 }));
        else await fetchTopics(topicMeta.page);
      } else if (faqs.length === 1 && faqMeta.page > 1) {
        setFaqMeta((p) => ({ ...p, page: p.page - 1 }));
        fetchTopics(topicMeta.page);
      } else {
        await refreshAll();
      }
    } catch (err) {
      setError('Delete failed. Please try again.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const kpis = [
    { label: 'Topics', value: stats.topics, icon: MdFolder, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
    { label: 'Total FAQs', value: stats.faqs, icon: MdQuestionAnswer, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)' },
    { label: 'Visible to customers', value: stats.activeFaqs, icon: MdHelp, gradient: 'linear-gradient(135deg,#10b981,#059669)' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>FAQs</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Group answers to common questions by topic for the customer app.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button startIcon={<MdFolder />} onClick={() => openTopic(null)} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', '&:hover': { background: '#f1f5f9' } }}>Add Topic</Button>
            <Button variant="contained" startIcon={<MdAdd />} disabled={!selectedTopic} onClick={() => openFaq(null)} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)' }, '&.Mui-disabled': { background: '#e2e8f0', border: '1px solid #cbd5e1' } }}>Add FAQ</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)' }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 2.5, alignItems: 'start' }}>
          {/* Topics */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#0f172a', mb: 1.5 }}>Topics</Typography>
            {loading.topics && topics.length === 0 ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1, borderRadius: '12px' }} />)
            ) : topics.length === 0 ? (
              <Typography sx={{ fontFamily: FONT, color: '#64748b', py: 3, textAlign: 'center' }}>No topics yet. Add the first one.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {topics.map((t) => {
                  const active = t.id === selectedTopic;
                  return (
                    <Box
                      key={t.id}
                      onClick={() => pickTopic(t.id)}
                      sx={{ p: 1.5, borderRadius: '12px', cursor: 'pointer', border: `1.5px solid ${active ? 'rgb(255 190 0)' : '#e2e8f0'}`, background: active ? 'rgb(255 209 2 / 12%)' : '#fff', opacity: t.isActive ? 1 : 0.65, transition: 'all .15s', '&:hover': { boxShadow: '0 4px 12px rgba(15,23,42,.08)' } }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.name}>{t.name}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{t.faqCount} FAQ{t.faqCount === 1 ? '' : 's'} · {t.activeFaqCount} visible · #{t.position}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit topic" arrow><IconButton size="small" onClick={() => openTopic(t)} sx={iconBtn('#ca8a04', '#fefce8')}><MdEdit size={15} /></IconButton></Tooltip>
                          <Tooltip title="Delete topic" arrow><IconButton size="small" onClick={() => setDeleteTarget({ kind: 'topic', item: t })} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={16} /></IconButton></Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          control={<Switch size="small" checked={t.isActive !== false} onChange={() => toggleTopic(t)} sx={switchSx} />}
                          label={<Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: '#475569' }}>{t.isActive ? 'Shown in app' : 'Hidden'}</Typography>}
                        />
                      </Box>
                    </Box>
                  );
                })}
                {topicMeta.totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Pagination count={topicMeta.totalPages} page={topicMeta.page} size="small" shape="rounded" siblingCount={0} onChange={(_, p) => setTopicMeta((m) => ({ ...m, page: p }))} />
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* FAQs of the selected topic */}
          <Box>
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ minWidth: 150 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a' }}>{selected ? selected.name : 'FAQs'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{selected ? `${faqMeta.total} question${faqMeta.total === 1 ? '' : 's'}` : 'Pick a topic'}</Typography>
              </Box>
              <TextField
                size="small" placeholder="Search questions or answers" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} disabled={!selectedTopic}
                sx={{ flex: 1, minWidth: 220 }}
                InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }}
              />
              <TextField select size="small" label="Status" value={statusFilter} disabled={!selectedTopic} onChange={(e) => { setStatusFilter(e.target.value); setFaqMeta((p) => ({ ...p, page: 1 })); }} sx={{ minWidth: 130 }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Hidden</MenuItem>
              </TextField>
            </Paper>

            {!selectedTopic ? (
              <Paper elevation={0} sx={{ py: 8, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#475569' }}>Select a topic to see its FAQs</Typography>
              </Paper>
            ) : loading.faqs ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={58} sx={{ mb: 1.25, borderRadius: '12px' }} />)
            ) : faqs.length === 0 ? (
              <Paper elevation={0} sx={{ py: 8, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#475569' }}>{search || statusFilter ? 'No FAQs match your filters.' : 'No FAQs in this topic yet.'}</Typography>
                {!search && !statusFilter && <Button onClick={() => openFaq(null)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}>Add the first FAQ</Button>}
              </Paper>
            ) : (
              <>
                {faqs.map((faq) => (
                  <Accordion
                    key={faq.id}
                    expanded={expanded === faq.id}
                    onChange={(_, open) => setExpanded(open ? faq.id : '')}
                    disableGutters
                    elevation={0}
                    sx={{ mb: 1.25, borderRadius: '12px !important', border: '1px solid #e2e8f0', opacity: faq.isActive ? 1 : 0.7, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<MdExpandMore size={22} />} sx={{ px: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1, minWidth: 0 }}>
                        <Box sx={{ minWidth: 28, height: 28, borderRadius: '8px', background: '#0f172a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontWeight: 800, fontSize: 11.5 }}>#{faq.position}</Box>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5, color: '#0f172a', flex: 1, minWidth: 0 }}>{faq.question}</Typography>
                        <StatusPill active={faq.isActive} />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', mb: 1.5 }}>{faq.answer}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={faq.isActive !== false} onChange={() => toggleFaq(faq)} sx={switchSx} />}
                          label={<Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, color: '#475569' }}>Show in app</Typography>}
                        />
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                          <Button size="small" startIcon={<MdEdit size={15} />} onClick={() => openFaq(faq)} sx={{ textTransform: 'none', fontWeight: 700, color: '#ca8a04', background: '#fefce8', borderRadius: '8px', px: 1.5 }}>Edit</Button>
                          <Button size="small" startIcon={<MdDeleteForever size={16} />} onClick={() => setDeleteTarget({ kind: 'faq', item: faq })} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,.1)', borderRadius: '8px', px: 1.5 }}>Delete</Button>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
                {faqMeta.totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={faqMeta.totalPages} page={faqMeta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setFaqMeta((m) => ({ ...m, page: p }))} sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black' } }} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Topic dialog */}
      <Dialog open={topicDialog.open} onClose={() => !saving && setTopicDialog((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
        <Box component="form" onSubmit={saveTopic}>
          <DialogHeader
            icon={MdFolder}
            accent="#6366f1"
            title={topicDialog.editingId ? 'Edit topic' : 'Add topic'}
            subtitle="A heading that groups related questions in the app."
            onClose={() => !saving && setTopicDialog((d) => ({ ...d, open: false }))}
          />
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '24px !important' }}>
            <TextField
              autoFocus fullWidth size="small" label="Topic name" placeholder="e.g. Orders & Delivery" required
              value={topicDialog.data.name} onChange={(e) => setTopicField({ name: e.target.value.slice(0, 60) })}
              error={!!topicDialog.errors.name}
              helperText={topicDialog.errors.name || `${topicDialog.data.name.length}/60`}
            />
            <TextField fullWidth size="small" type="number" label="Position" inputProps={{ min: 0 }} value={topicDialog.data.position} onChange={(e) => setTopicField({ position: e.target.value })} helperText="Lower numbers are listed first" />
            <Box sx={{ p: 1.5, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch checked={topicDialog.data.isActive} onChange={(e) => setTopicField({ isActive: e.target.checked })} sx={switchSx} />}
                label={<Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5 }}>Show this topic in the app</Typography>}
              />
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', ml: 0.5 }}>Turn off to keep it as a draft while you add questions.</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>Preview</Typography>
              <Box sx={{ p: 1.5, borderRadius: '12px', border: '1.5px solid rgb(255 190 0)', background: 'rgb(255 209 2 / 12%)', opacity: topicDialog.data.isActive ? 1 : 0.6 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{topicDialog.data.name || 'Topic name'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>#{Number(topicDialog.data.position) || 0} · {topicDialog.data.isActive ? 'Shown in app' : 'Hidden'}</Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setTopicDialog((d) => ({ ...d, open: false }))} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
              {saving ? 'Saving…' : topicDialog.editingId ? 'Update topic' : 'Add topic'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* FAQ dialog */}
      <Dialog open={faqDialog.open} onClose={() => !saving && setFaqDialog((d) => ({ ...d, open: false }))} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
        <Box component="form" onSubmit={saveFaq}>
          <DialogHeader
            icon={MdQuestionAnswer}
            accent="#0ea5e9"
            title={faqDialog.editingId ? 'Edit FAQ' : 'Add FAQ'}
            subtitle="Write the question the way customers ask it, then a short, clear answer."
            onClose={() => !saving && setFaqDialog((d) => ({ ...d, open: false }))}
          />
          <DialogContent sx={{ pt: '24px !important' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField select fullWidth size="small" label="Topic" required value={faqDialog.data.topic} onChange={(e) => setFaqField({ topic: e.target.value })} error={!!faqDialog.errors.topic} helperText={faqDialog.errors.topic}>
                  {topics.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
                <TextField
                  fullWidth size="small" label="Question" required placeholder="e.g. How do I track my order?"
                  value={faqDialog.data.question} onChange={(e) => setFaqField({ question: e.target.value.slice(0, 200) })}
                  error={!!faqDialog.errors.question} helperText={faqDialog.errors.question || `${faqDialog.data.question.length}/200`}
                />
                <TextField
                  fullWidth size="small" label="Answer" required multiline minRows={6} placeholder="Keep it short and step-by-step where possible."
                  value={faqDialog.data.answer} onChange={(e) => setFaqField({ answer: e.target.value.slice(0, 2000) })}
                  error={!!faqDialog.errors.answer} helperText={faqDialog.errors.answer || `${faqDialog.data.answer.length}/2000`}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" type="number" label="Position" inputProps={{ min: 0 }} value={faqDialog.data.position} onChange={(e) => setFaqField({ position: e.target.value })} sx={{ width: 130 }} />
                  <FormControlLabel
                    control={<Switch checked={faqDialog.data.isActive} onChange={(e) => setFaqField({ isActive: e.target.checked })} sx={switchSx} />}
                    label={<Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 12.5 }}>Show in app</Typography>}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>How customers see it</Typography>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', opacity: faqDialog.data.isActive ? 1 : 0.6 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#0ea5e9', mb: 0.75 }}>
                    {topics.find((t) => t.id === faqDialog.data.topic)?.name || 'Topic'}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#0f172a', lineHeight: 1.35 }}>
                    {faqDialog.data.question || 'Your question appears here'}
                  </Typography>
                  <Box sx={{ my: 1.25, borderTop: '1px dashed #cbd5e1' }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#334155', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {faqDialog.data.answer || 'The answer appears here as you type.'}
                  </Typography>
                </Paper>
                {!faqDialog.data.isActive && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#92400e', mt: 1 }}>Hidden — customers won't see this until you switch it on.</Typography>}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setFaqDialog((d) => ({ ...d, open: false }))} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
              {saving ? 'Saving…' : faqDialog.editingId ? 'Update FAQ' : 'Add FAQ'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete {deleteTarget?.kind === 'topic' ? 'topic' : 'FAQ'}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT }}>
            {deleteTarget?.kind === 'topic'
              ? <><b>{deleteTarget.item.name}</b> will be removed{deleteTarget.item.faqCount ? <> together with its <b>{deleteTarget.item.faqCount} FAQ{deleteTarget.item.faqCount === 1 ? '' : 's'}</b></> : ''}. This can't be undone.</>
              : <><b>{deleteTarget?.item.question}</b> will be removed from the app. This can't be undone.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Yes, delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FAQs;
